import { DisplayPrice } from '@models/vertical/displayPrice.model';

/** The percentile band the vertical page renders. */
export interface ResolvedPriceBand {
  q25: number;
  q50: number;
  q75: number;
}

const isPublishable = (value?: number): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const CONFIDENCE_TIERS = ['high', 'medium', 'low'] as const;

type ConfidenceTier = (typeof CONFIDENCE_TIERS)[number];

const isKnownConfidence = (value: unknown): value is ConfidenceTier =>
  typeof value === 'string' &&
  (CONFIDENCE_TIERS as readonly string[]).includes(value);

/**
 * Turn a stored `displayPrice` into the band the vertical page renders,
 * honouring the confidence and unit contract in DISPLAY-PRICE-SPEC.md.
 *
 * Returns null whenever the stored value cannot be published as-is, so the
 * caller leaves the page exactly as it would have been without the field.
 */
export function resolveDisplayPriceBand(
  stored?: DisplayPrice,
): ResolvedPriceBand | null {
  if (!stored) {
    return null;
  }
  const { min, max, middle, confidence, unit } = stored;

  if (!isPublishable(min) || !isPublishable(max) || max < min) {
    return null;
  }

  // The price widget renders a bare amount with no unit, so an hourly rate
  // would read as a per-task price. Skip until the display carries the unit.
  if (unit !== 'task') {
    return null;
  }

  // The field is written by a job in another repo and reaches us unvalidated,
  // so an unrecognised tier means we cannot tell how far to trust `middle`.
  // Publish nothing rather than assume the lowest tier — same posture as the
  // unit check above.
  if (!isKnownConfidence(confidence)) {
    return null;
  }

  // `middle` is only trustworthy at high confidence — see the confidence table
  // in the spec. Below that, publish the low end rather than a median we have
  // already declared unreliable.
  const typical =
    confidence === 'high' && isPublishable(middle) && middle >= min && middle <= max
      ? middle
      : min;

  return { q25: min, q50: typical, q75: max };
}
